/* Vague 18.3 — Hook `useLLMJobs` + tray flottant `<LLMJobsTray />`
 *
 * Hook léger qui poll /api/workspace/llm-jobs?status_filter=active toutes
 * les 3 secondes et expose la liste + helpers.
 *
 * Le tray s'affiche en bas à droite quand des jobs sont actifs ou récemment
 * terminés. Cliquer un job 'done' navigue vers le contexte associé (carto 360°
 * pour phantom_scan, origination pour friction_gap).
 */
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { notify } from "@/lib/notify";
import {
  Loader2, CheckCircle, AlertTriangle, X as XIcon, Sparkles, Ghost, Flame, ChevronUp, ChevronDown,
} from "lucide-react";

const POLL_INTERVAL_MS = 3000;
const RECENT_DONE_WINDOW_MS = 30_000; // garder les jobs done/failed visibles 30s

const JOB_TYPE_META = {
  friction_gap: { label: "Friction Gap", icon: Flame, color: "#EF9F27" },
  phantom_scan: { label: "Phantom Scan", icon: Ghost, color: "#A78BFA" },
};

const JobsCtx = createContext({ jobs: [], startedAt: {}, dismiss: () => {}, refresh: () => {} });

export function LLMJobsProvider({ children }) {
  const [jobs, setJobs] = useState([]);
  const [dismissed, setDismissed] = useState({}); // job_id -> true
  const prevStatusRef = useRef({}); // job_id -> status (pour détecter transitions)

  const refresh = useCallback(async () => {
    try {
      // On récupère active + done/failed récents
      const r = await api.get("/workspace/llm-jobs", { params: { limit: 20 } });
      const items = r.data?.items || [];
      // Détecte les transitions (running → done/failed) pour notifier
      const prev = prevStatusRef.current;
      for (const j of items) {
        const old = prev[j.id];
        if (old && old !== j.status && (j.status === "done" || j.status === "failed")) {
          const meta = JOB_TYPE_META[j.type] || { label: j.type };
          if (j.status === "done") {
            notify.success(`${meta.label} terminé.`);
          } else {
            notify.error(`${meta.label} a échoué : ${j.error || "erreur inconnue"}`);
          }
        }
        prev[j.id] = j.status;
      }
      setJobs(items);
    } catch {
      // silencieux : tray invisible si pas connecté
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const dismiss = useCallback((jobId) => {
    setDismissed((d) => ({ ...d, [jobId]: true }));
    api.delete(`/workspace/llm-jobs/${jobId}`).catch(() => {});
  }, []);

  // Filtre : actif OU done/failed dans les 30s + non dismissé
  const visibleJobs = jobs.filter((j) => {
    if (dismissed[j.id]) return false;
    if (j.status === "queued" || j.status === "running") return true;
    const ts = j.finished_at ? new Date(j.finished_at).getTime() : 0;
    return ts > Date.now() - RECENT_DONE_WINDOW_MS;
  });

  return (
    <JobsCtx.Provider value={{ jobs: visibleJobs, dismiss, refresh }}>
      {children}
      <LLMJobsTray jobs={visibleJobs} dismiss={dismiss} />
    </JobsCtx.Provider>
  );
}

export function useLLMJobs() {
  return useContext(JobsCtx);
}

/**
 * Démarre un job et retourne le job_id. Le tray prend la suite (polling +
 * notif automatique au done/failed).
 */
export async function startLLMJob(jobType, params) {
  const r = await api.post(`/workspace/llm-jobs/${jobType}/start`, { params });
  return r.data;
}

// ============================================================================
// Tray flottant en bas à droite
// ============================================================================
function LLMJobsTray({ jobs, dismiss }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  if (!jobs || jobs.length === 0) return null;

  const goToJob = (j) => {
    if (j.status !== "done" || !j.result) return;
    if (j.type === "phantom_scan") {
      const st = j.params?.subject_type;
      const sid = j.params?.subject_id;
      if (st && sid) navigate(`/app/cartographie/${st}/${sid}`);
    } else if (j.type === "friction_gap") {
      // Le snapshot est persisté → la heatmap se recharge avec
      navigate("/app/origination");
    }
  };

  return (
    <div
      className="fixed bottom-4 right-4 z-[60] w-80 max-w-[95vw]"
      data-testid="llm-jobs-tray"
    >
      <div className="noam-card border border-[#7F77DD]/30 shadow-2xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-3 py-2 text-xs bg-[#7F77DD]/10 hover:bg-[#7F77DD]/15 transition"
          onClick={() => setCollapsed((c) => !c)}
          data-testid="llm-jobs-tray-toggle"
        >
          <span className="flex items-center gap-1.5 text-[#7F77DD] font-medium">
            <Sparkles className="w-3.5 h-3.5" />
            {jobs.length} analyse{jobs.length > 1 ? "s" : ""} {jobs.some((j) => j.status === "running" || j.status === "queued") ? "en cours" : "récente(s)"}
          </span>
          {collapsed ? <ChevronUp className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />}
        </button>
        {!collapsed && (
          <div className="max-h-72 overflow-y-auto divide-y divide-white/5" data-testid="llm-jobs-tray-list">
            {jobs.map((j) => {
              const meta = JOB_TYPE_META[j.type] || { label: j.type, icon: Sparkles, color: "#7F77DD" };
              const Icon = meta.icon;
              const isActive = j.status === "queued" || j.status === "running";
              const isDone = j.status === "done";
              return (
                <div
                  key={j.id}
                  className="px-3 py-2 text-xs"
                  data-testid={`llm-jobs-tray-item-${j.id}`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: meta.color }} />
                    <button
                      onClick={() => goToJob(j)}
                      disabled={!isDone}
                      className={`flex-1 min-w-0 text-left ${isDone ? "cursor-pointer hover:text-zinc-100" : ""}`}
                    >
                      <div className="font-medium text-zinc-200 truncate">{meta.label}</div>
                      <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-1.5">
                        {isActive && <Loader2 className="w-3 h-3 animate-spin text-[#7F77DD]" />}
                        {isDone && <CheckCircle className="w-3 h-3 text-[#1D9E75]" />}
                        {j.status === "failed" && <AlertTriangle className="w-3 h-3 text-red-400" />}
                        <span className="truncate">{j.progress_msg || j.status}</span>
                      </div>
                      {j.status === "failed" && j.error && (
                        <div className="text-[10px] text-red-300 mt-1 truncate" title={j.error}>{j.error}</div>
                      )}
                    </button>
                    <button
                      onClick={() => dismiss(j.id)}
                      className="p-1 hover:bg-white/10 rounded flex-shrink-0"
                      title="Masquer"
                      data-testid={`llm-jobs-tray-dismiss-${j.id}`}
                    >
                      <XIcon className="w-3 h-3 text-zinc-500" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
